import React from 'react';
import {createSelector} from 'reselect';
import {useSelector} from 'react-redux';
import {LinkProps} from 'react-router-dom';

import {defaultTheme} from '@ibibo/build-theme';

import * as S from './styles';

import {
  hotelArea as cityNameSelectorFn,
  cityId as cityIdSelectorFn,
  hotelCityURLSelectorFn,
  hotelName as hotelNameSelectorFn,
  isStaticDataLoading as isStaticDataLoadingSelectorFn,
  countryUrlSelector,
  countryNameSelector,
} from '../../selectors';

import {
  checkinSelectorFn,
  checkoutSelectorFn,
  roomStringSelectorFn,
} from '../../../common/redux/selectors';

import {parseDateIntoHermesString} from '../../../../utils/date-utils';
import makeSrpUrl from '../../../../utils/makeSrpUrl';

import ArrowRight from '../../../../../stories/Icons/ArrowRightDetailIcon';

type BreadCrumbProps = {};

type BreadCrumbItemType = {
  children: React.ReactNode;
  to: LinkProps['to'];
  positionForStructuredSEO: string;
};

type BreadCrumbItemAnchorType = {
  children: React.ReactText;
  href: string;
  positionForStructuredSEO: string;
};

export const listItemSEOProps = {
  itemScope: true,
  itemProp: 'itemListElement',
  itemType: 'http://schema.org/ListItem',
};

const BreadCrumbItemAnchor: React.FC<BreadCrumbItemAnchorType> = ({
  children,
  href,
  positionForStructuredSEO,
}) => (
  <li {...listItemSEOProps}>
    <S.BreadCrumbItemAnchorTag href={href} itemProp="item">
      <span itemProp="name">{children}</span>
    </S.BreadCrumbItemAnchorTag>
    <meta itemProp="position" content={positionForStructuredSEO} />
  </li>
);

type NativeAnchorBreadCrumbItemType = {
  children: React.ReactNode;
  href: string;
  positionForStructuredSEO: string;
};

const NativeAnchorBreadCrumbItem: React.FC<NativeAnchorBreadCrumbItemType> = ({
  children,
  href,
  positionForStructuredSEO,
}) => (
  <li {...listItemSEOProps}>
    <S.BreadCrumbATagItemLink href={href} itemProp="item">
      <span itemProp="name">{children}</span>
    </S.BreadCrumbATagItemLink>
    <meta itemProp="position" content={positionForStructuredSEO} />
  </li>
);

type NoLinkBreadcrumbItemTypes = {
  children: React.ReactNode;
  positionForStructuredSEO: string;
};

const NoLinkBreadcrumbItem: React.FC<NoLinkBreadcrumbItemTypes> = ({
  children,
  positionForStructuredSEO,
}) => (
  <S.NoLinkBreadcrumbListItem {...listItemSEOProps}>
    <span itemProp="name">{children}</span>
    <meta itemProp="position" content={positionForStructuredSEO} />
  </S.NoLinkBreadcrumbListItem>
);

const RightArrow = () => (
  <S.ArrowWrapper>
    <ArrowRight width={'0.7rem'} fill={defaultTheme.colors.grey.light9b} />
  </S.ArrowWrapper>
);

const breadcrumbSelector = createSelector(
  [
    cityNameSelectorFn,
    cityIdSelectorFn,
    hotelCityURLSelectorFn,
    hotelNameSelectorFn,
    checkinSelectorFn,
    checkoutSelectorFn,
    roomStringSelectorFn,
    countryUrlSelector,
    countryNameSelector,
  ],
  (
    cityName,
    cityId,
    hotelCityURL,
    hotelName,
    checkinObj,
    checkoutObj,
    roomString,
    countryUrl,
    countryName,
  ) => {
    const checkin = parseDateIntoHermesString(checkinObj.date);
    const checkout = parseDateIntoHermesString(checkoutObj.date);

    return {
      cityName,
      cityId,
      hotelCityURL,
      hotelName,
      checkin,
      checkinDateObj: checkinObj,
      checkout,
      checkoutDateObj: checkoutObj,
      roomString,
      countryUrl,
      countryName,
    };
  },
);

const Breadcrumb: React.FC<BreadCrumbProps> = () => {
  const {
    cityName,
    cityId,
    hotelCityURL = '',
    hotelName = '',
    checkinDateObj,
    checkoutDateObj,
    roomString,
    countryUrl,
    countryName
  } = useSelector(breadcrumbSelector);

  const isStaticDataLoading = useSelector(isStaticDataLoadingSelectorFn);

  const srpCityURL = hotelCityURL;

  if (isStaticDataLoading) {
    return null;
    // return <BreadcrumbsDetailsLoader />;
  }

  const showCountry = !!countryUrl;
  const showCity = !!cityName && srpCityURL;

  let positionForStructuredSEOForHotelName = '3';

  if (showCountry && showCity) {
    positionForStructuredSEOForHotelName = '5';
  }

  if ((!showCountry && showCity) || (showCountry && !showCity)) {
    positionForStructuredSEOForHotelName = '4';
  }

  const toSRP = () => {
    const url = makeSrpUrl({
      checkin: checkinDateObj,
      checkout: checkoutDateObj,
      roomString,
      destination: {
        n: cityName,
        _id: cityId,
        xtr: {
          cc: (countryName.toLowerCase() === 'india' ? 'in' : '')
        }
      },
    });
    window.open(url, '_self');
  }

  return (
    <>
    <S.OuterWrapperDiv>
      <S.BreadCrumbContainer itemScope itemType="https://schema.org/breadcrumb">

        <S.BreadCrumbList itemScope itemType="http://schema.org/BreadcrumbList">
          <BreadCrumbItemAnchor href="/hotels/v1/" positionForStructuredSEO="1">
            Home
          </BreadCrumbItemAnchor>

          {showCity && (
            <div onClick={toSRP}>
              <NativeAnchorBreadCrumbItem
                positionForStructuredSEO={countryUrl ? '4' : '3'}
                href="javascript:void(0);"
              >
                <RightArrow />
                <span>{cityName}</span>
              </NativeAnchorBreadCrumbItem>
            </div>
          )}

          {!!hotelName && (
            <NoLinkBreadcrumbItem positionForStructuredSEO={positionForStructuredSEOForHotelName}>
              <RightArrow />
              <span>{hotelName}</span>
            </NoLinkBreadcrumbItem>
          )}

        </S.BreadCrumbList>
      </S.BreadCrumbContainer>
    </S.OuterWrapperDiv>
    </>
  );
};

export default Breadcrumb;
